import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Mail, Lock, ArrowRight, Loader2, KeyRound } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PartnerPortalLogin() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Email & password required');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/partner-portal/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Invalid credentials');
      localStorage.setItem('partner_portal_token', data.access_token);
      localStorage.setItem('partner_portal_partner', JSON.stringify(data.partner));
      toast.success(`Welcome, ${data.partner.name}!`);
      navigate('/partner-portal');
    } catch (err) {
      toast.error(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 flex items-center justify-center p-4"
         data-testid="partner-portal-login">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-indigo-500/10 border border-indigo-400/30 mb-3">
            <KeyRound className="h-7 w-7 text-indigo-300" />
          </div>
          <h1 className="text-2xl font-bold text-white">Distributor Portal</h1>
          <p className="text-indigo-200/70 text-sm mt-1">Master &amp; Sub Distributor · Watcho · Wallet · Recharge</p>
        </div>

        <Card className="bg-white/95 backdrop-blur shadow-2xl border-0">
          <CardHeader>
            <CardTitle>Sign In</CardTitle>
            <CardDescription>Use the credentials provided by ANT Broadband admin.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="email" required value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9" placeholder="partner@your-company.com"
                    data-testid="pp-login-email"
                  />
                </div>
              </div>
              <div>
                <Label>Password</Label>
                <div className="relative">
                  <Lock className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    type="password" required value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9" placeholder="••••••••"
                    data-testid="pp-login-password"
                  />
                </div>
              </div>
              <Button type="submit" disabled={loading}
                      className="w-full bg-indigo-600 hover:bg-indigo-700"
                      data-testid="pp-login-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Sign in <ArrowRight className="h-4 w-4 ml-1" /></>}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-xs text-indigo-200/60 mt-6">
          Forgot your password? Contact your ANT Broadband account manager to reset.
        </p>
      </div>
    </div>
  );
}
